import { createUUID } from "node-library/dist/helpers/request_helper";
import {
	FrontendOrganization,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GreenButton } from "../../../../common-components/button";
import {
	CustomDropdown,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";

interface Props {
	addOrEditOrganization: (org: FrontendOrganization) => void;

	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	salesReps: Array<FrontendUserInfo>;
	organization?: FrontendOrganization;
}

export default function AddOrEditOrganizationModal(props: Props) {
	const { t: translation } = useTranslation();

	const { open, setOpen } = props;

	//org address info
	const [clientCompanyName, setClientCompanyName] = useState<string>(
		props.organization?.clientCompanyName || ""
	);

	const [address, setAddress] = useState<string>(
		props.organization?.address || ""
	);
	const [city, setCity] = useState<string>(props.organization?.city || "");
	const [province, setProvince] = useState<string>(
		props.organization?.province || ""
	);
	const [postalCode, setPostalCode] = useState<string>(
		props.organization?.postalCode || ""
	);
	const [country, setCountry] = useState<string>(
		props.organization?.country || ""
	);

	const [salesUserId, setSalesUserId] = useState<string>(
		props.organization?.salesUserId || ""
	);

	const [existingCompany, setExistingCompany] = useState<boolean>(false);
	//validations
	const [clientCompanyNameErrorMsg, setClientCompanyNameErrorMsg] =
		useState<string>("");

	const [addressErrorMsg, setAddressErrorMsg] = useState<string>("");
	const [cityErrorMsg, setCityErrorMsg] = useState<string>("");
	const [provinceErrorMsg, setProvinceErrorMsg] = useState<string>("");
	const [postalCodeErrorMsg, setPostalCodeErrorMsg] = useState<string>("");
	const [countryErrorMsg, setCountryErrorMsg] = useState<string>("");

	const [ready, setReady] = useState<boolean>(false);

	const fieldsForRegistration: Array<any> = [
		{
			id: "client-company-name-input",

			label: `${translation("CompanyName")}`,
			inputType: "text",
			value: clientCompanyName,
			setValue: setClientCompanyName,
			required: true,
			errorMsg: clientCompanyNameErrorMsg,
			setErrorMsg: setClientCompanyNameErrorMsg,
		},

		{
			id: "client-address-input",

			label: `${translation("Address")}`,
			inputType: "text",
			value: address,
			setValue: setAddress,
			required: true,
			errorMsg: addressErrorMsg,
			setErrorMsg: setAddressErrorMsg,
		},
		{
			id: "client-city-input",

			label: `${translation("City")}`,
			inputType: "text",
			value: city,
			setValue: setCity,
			required: true,
			errorMsg: cityErrorMsg,
			setErrorMsg: setCityErrorMsg,
		},
		{
			id: "client-postal-code-input",
			label: `${translation("PostalCodeOrZip")}`,
			inputType: "text",
			value: postalCode,
			setValue: setPostalCode,

			required: true,
			errorMsg: postalCodeErrorMsg,
			setErrorMsg: setPostalCodeErrorMsg,
		},
		{
			id: "client-country-input",
			label: `${translation("Country")}`,
			inputType: "text",
			value: country,
			setValue: setCountry,

			required: true,
			errorMsg: countryErrorMsg,
			setErrorMsg: setCountryErrorMsg,
		},
		{
			id: "client-province-input",

			label: `${translation("ProvinceOrState")}`,
			inputType: "text",
			value: province,
			setValue: setProvince,

			required: true,
			errorMsg: provinceErrorMsg,
			setErrorMsg: setProvinceErrorMsg,
		},
	];

	useEffect(() => {
		if (open) {
			for (const field of fieldsForRegistration) {
				field.setErrorMsg(translation(""));
			}

			//client address info
			setExistingCompany(false);
			setClientCompanyName(props.organization?.clientCompanyName || "");
			setAddress(props.organization?.address || "");
			setCity(props.organization?.city || "");
			setProvince(props.organization?.province || "");
			setPostalCode(props.organization?.postalCode || "");
			setCountry(props.organization?.country || "");

			setSalesUserId(props.organization?.salesUserId || "");

			setReady(true);
		} else {
			setReady(false);
		}
	}, [open]);

	function handleSubmit() {
		let hasError: boolean = false;
		for (const field of fieldsForRegistration) {
			if (
				field.required &&
				(!field.value || (field.value as string).trim().length == 0)
			) {
				field.setErrorMsg(translation("MissingInformation"));
				hasError = true;
			} else {
				field.setErrorMsg("");
			}
		}

		if (hasError) {
			return;
		}

		let newOrg: FrontendOrganization;
		if (props.organization) {
			const rep = props.salesReps.find((rep: FrontendUserInfo) => {
				return rep.userId === salesUserId;
			});
			const repName = rep
				? `${rep.firstName || ""} ${rep.lastName || ""}`.trim()
				: translation("None");
			newOrg = {
				...props.organization,
				clientCompanyName,
				address,
				city,
				province,
				postalCode,
				country,
				salesUserId,
				salesFullName: repName,
			};
		} else {
			newOrg = {
				organizationId: createUUID(),
				associationId: -1,
				clientCompanyName,
				address,
				city,
				province,
				postalCode,
				country,
				salesUserId: salesUserId,
				salesFullName: "",
				inserted: new Date(),
			};
		}
		props.addOrEditOrganization(newOrg);
		setOpen(false);
	}

	const registrationInputs: Array<React.ReactNode> = fieldsForRegistration.map(
		(field, index) => {
			return (
				<CustomTextInput
					id={field.id}
					key={field.id}
					value={field.value}
					setValue={field.setValue}
					disabled={field.disabled}
					label={field.label}
					type={field.inputType}
					required={field.required}
					errorMsg={field.errorMsg}
					setErrorMsg={field.setErrorMsg}
				></CustomTextInput>
			);
		}
	);

	const registrationContent: React.ReactNode = (
		<div className="registration-input-content">
			{registrationInputs[0]}
			{registrationInputs[1]}

			<div className="register-grid-row">
				{registrationInputs[2]}
				{registrationInputs[3]}
			</div>
			<div className="register-grid-row">
				{registrationInputs[4]}
				{registrationInputs[5]}
			</div>
			<CustomDropdown
				id="client-sales-rep-dropdown"
				label={translation("SalesRep")}
				options={props.salesReps.map((rep: FrontendUserInfo) => {
					return {
						title: `${rep.firstName || ""} ${rep.lastName || ""}`.trim(),
						value: rep.userId,
					};
				})}
				value={salesUserId}
				setValue={setSalesUserId}
			></CustomDropdown>
		</div>
	);

	const confirmApprovalModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={
				props.organization
					? translation("EditOrganization")
					: translation("NewOrganization")
			}
			// headerDescription={translation("ConfirmApprovalDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-client-change-button"
					id="submit-client-change-button"
					children={
						props.organization ? translation("Edit") : translation("Add")
					}
					onClick={handleSubmit}
				></GreenButton>,
			]}
		>
			{registrationContent}
		</ModalWithButtons>
	);
	return confirmApprovalModal;
}
